<script setup lang="ts">
import { ref, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'

const {
    Icon,
} = UIKit

const props = withDefaults(defineProps<{
    isDarkMode?: boolean
    status?: 'active' | 'complete'
    levelNum?: number
    percentStart?: number
    percentStop?: number
    animate?: boolean
    completedAllSubjects?: boolean
}>(), {
    isDarkMode: false,
    status: 'active',
    levelNum: 1,
    percentStart: 0,
    percentStop: 100,
    animate: false,
    completedAllSubjects: false,
})

const id = `quizResultLevelBadge-${Math.round(Math.random() * 1e5)}`
const percentDecimal = ref(0)
const animationStarted = ref(false)

onMounted(() => {
    percentDecimal.value = props.percentStart / 100

    if (!props.completedAllSubjects) {
        draw()
    }
})

const draw = () => {
    const radiansComplete = percentDecimal.value * 2 * Math.PI
    const canvas = document.getElementById(id) as HTMLCanvasElement
    const ctx = canvas?.getContext('2d')
    if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // Dark fill, according to percent
        ctx.beginPath()
        const radius = 460
        ctx.arc(600, 600, radius, -(Math.PI / 2), radiansComplete - (Math.PI / 2))
        ctx.lineWidth = 280
        ctx.strokeStyle = '#D10FE5'
        ctx.globalAlpha = 1
        ctx.stroke()
    }

    const animationRateNum = 0.02
    if (props.animate && percentDecimal.value < (props.percentStop / 100)) {
        const animationRate = props.percentStop >= 100 ? animationRateNum * 2 : animationRateNum
        percentDecimal.value += animationRate
        if (!animationStarted.value) {
            setTimeout(() => {
                window.requestAnimationFrame(draw)
                animationStarted.value = true
            }, 500)
        } else {
            window.requestAnimationFrame(draw)
        }
    }
}

</script>

<template>
    <div
        class="quiz-result-level-badge"
        :class="{
            'quiz-result-level-badge--active': status === 'active',
            'quiz-result-level-badge--complete': status === 'complete',
            'quiz-result-level-badge--completed-all-subjects': completedAllSubjects,
            'quiz-result-level-badge--dark': isDarkMode,
        }"
    >
        <div>
            <div
                class="quiz-result-level-badge__gauge-background-white"
            />
            <div
                class="quiz-result-level-badge__gauge-background"
                :class="{
                    'quiz-result-level-badge__gauge-background--complete': status === 'complete',
                    'quiz-result-level-badge__gauge-background--completed-all-subjects': completedAllSubjects,
                    'quiz-result-level-badge__gauge-background--dark': isDarkMode,
                }"
            />
        </div>
        <canvas
            v-if="status === 'active'"
            class="quiz-result-level-badge__gauge"
            :id="id"
            width="1200"
            height="1200"
        />
        <div
            class="quiz-result-level-badge__level-num"
            :class="{
                'quiz-result-level-badge__level-num--active': status === 'active',
                'quiz-result-level-badge__level-num--complete': status === 'complete',
                'quiz-result-level-badge__level-num--completed-all-subjects': completedAllSubjects,
                'quiz-result-level-badge__level-num--dark': isDarkMode,
            }"
        >
            <template v-if="completedAllSubjects">
                <Icon
                    class="quiz-result-level-badge__check"
                    :class="{
                        'quiz-result-level-badge__check--dark': isDarkMode,
                    }"
                    type="correct"
                />
            </template>
            <template v-else>
                {{ levelNum }}
            </template>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.quiz-result-level-badge {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    overflow: hidden;
    width: 120px;
    height: 120px;

    &--active {
        border: 1px solid $brand-purple;
    }

    &--complete,
    &--completed-all-subjects {
        border: 1px solid $jungle-green;
    }

    &--complete#{&}--dark,
    &--completed-all-subjects#{&}--dark {
        border: 1px solid $cadaverous;
    }

    &__gauge-background-white,
    &__gauge-background {
        z-index: 1;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 100%;
        width: 80px;
        height: 80px;
    }

    &__gauge-background-white {
        border: 30px solid $white;
    }

    &__gauge-background {
        border: 30px solid rgba($brand-purple, 0.3);

        &--complete,
        &--completed-all-subjects {
            border: 30px solid $jungle-green;
        }

        &--complete#{&}--dark,
        &--completed-all-subjects#{&}--dark {
            border: 30px solid $cadaverous;
        }
    }

    &__gauge {
        z-index: 2;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 150px;
        height: 150px;
    }

    &__level-num {
        z-index: 3;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 100%;
        width: 79px;
        height: 79px;
        font-size: 40px;
        font-weight: 700;
        letter-spacing: -2px;

        &--active {
            border: 1px solid $brand-purple;
        }

        &--complete,
        &--completed-all-subjects {
            border: 1px solid $jungle-green;
        }

        &--complete#{&}--dark,
        &--completed-all-subjects#{&}--dark {
            border: 1px solid $cadaverous;
        }

        &--dark {
            color: $white;
        }
    }

    &__check {
        color: $brand-black;
        width: 30px;
        height: 34px;
        height: 100%;

        &--dark {
            color: $white;
        }
    }
}
</style>
